import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';

/**
 * Legacy-migration helper filter panel for the `submissions-dasboard.list`
 * screen ("Tax Payer Review / Maintenance"). The original AngularJS template
 * (`ui/app/components/efile/efileElements/list-tpl.html`) rendered a custom
 * three-control header (refresh, tax year, submission type, filter button)
 * directly inside the screen template — it is not a regular server-side
 * filter panel, so `<gtw-data-filters>` cannot reproduce it.
 *
 * We mirror the legacy behavior by parking the user selection on
 * `activeScreen.legacyFilterParams` and asking the workspace to refresh the
 * grid; `GridDirective.loadGridData` then merges those params on top of the
 * defaults declared in `LEGACY_ROUTE_API_GET`.
 */
@Component({
    selector: 'gtw-submission-list-filter',
    templateUrl: './submission-list-filter.component.html',
    styleUrls: ['./submission-list-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SubmissionListFilterComponent implements OnInit, OnDestroy {
  taxYears: any[] = [];
  selectedTaxYearValue: string = '';
  submissionType: 'federal' | 'state' = 'federal';
  filterMatch: boolean = true;

  private sub = new Subscription();

  constructor(
    private globalService: GlobalService,
    private workspaceService: WorkspaceService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.hydrateFromGlobal();
    // If the user switches the globally-selected tax year from the app header,
    // keep the dropdown in sync (without clobbering a local pick the user
    // made on this screen).
    this.sub.add(
      this.globalService.globalParamsChanged$.subscribe(() => {
        this.hydrateFromGlobal(true);
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private hydrateFromGlobal(preserveLocal: boolean = false): void {
    const input = this.globalService.global?.inputs?.tax_year;
    this.taxYears = Array.isArray(input?.values) ? input.values : [];

    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
    const persisted = activeScreen?.legacyFilterParams;

    if (!preserveLocal || !persisted) {
      const globalValue = input?.selected?.value ?? input?.values?.[0]?.value ?? '';
      this.selectedTaxYearValue = persisted?.tax_year ?? String(globalValue ?? '');
      this.submissionType = (persisted?.type as any) ?? 'federal';
    }

    this.persistCurrent(activeScreen);
    this.filterMatch = true;
  }

  private persistCurrent(activeScreen: any): void {
    if (!activeScreen) return;
    activeScreen.legacyFilterParams = {
      tax_year: this.selectedTaxYearValue,
      type: this.submissionType,
    };
  }

  onTaxYearChange(value: string): void {
    this.selectedTaxYearValue = value;
    this.filterMatch = false;
  }

  onTypeChange(value: string): void {
    this.submissionType = (value as 'federal' | 'state') || 'federal';
    this.filterMatch = false;
  }

  refresh(): void {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
    if (!activeScreen) return;
    this.persistCurrent(activeScreen);
    this.workspaceService.clearCache(activeScreen);
    this.workspaceService.dataFetched.next(true);
    this.workspaceService.refreshGridData.next(activeScreen);
    this.filterMatch = true;
    this.cdr.markForCheck();
  }
}
